import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createServiceType,
  type CreateServiceTypeResult,
} from '../api/createServiceTypeApiService';
import { SERVICE_TYPE_LABELS } from '../constants/labels';
import { validateDescription, validateServiceName } from '../utils/validation';

export interface CreateServiceTypeFormData {
  serviceName: string;
  description: string;
}

export interface CreateServiceTypeFormErrors {
  serviceName?: string;
  description?: string;
}

const LIST_PATH = '/Typy-usług';

export const useCreateServiceType = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<CreateServiceTypeFormData>({
    serviceName: '',
    description: '',
  });
  const [errors, setErrors] = useState<CreateServiceTypeFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');

  const handleServiceNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value.trimStart().slice(0, 50);
      setFormData((prev) => ({ ...prev, serviceName: next }));
      if (errors.serviceName) {
        setErrors((prev) => {
          const { serviceName: _s, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.serviceName]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value.trimStart().slice(0, 500);
      setFormData((prev) => ({ ...prev, description: next }));
      if (errors.description) {
        setErrors((prev) => {
          const { description: _d, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.description]
  );

  const applyFieldErrors = useCallback(
    (fieldErrors: Record<string, string>): boolean => {
      const mapped: CreateServiceTypeFormErrors = {};
      if (fieldErrors['service_name']) {
        mapped.serviceName = fieldErrors['service_name'];
      }
      if (fieldErrors['description']) {
        mapped.description = fieldErrors['description'];
      }
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
        return true;
      }
      return false;
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const nextErrors: CreateServiceTypeFormErrors = {};
      const nameError = validateServiceName(formData.serviceName);
      if (nameError) {
        nextErrors.serviceName = nameError;
      }
      const descriptionError = validateDescription(formData.description);
      if (descriptionError) {
        nextErrors.description = descriptionError;
      }

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        const result: CreateServiceTypeResult = await createServiceType({
          serviceName: formData.serviceName.trim(),
          description: formData.description.trim(),
        });

        if (result.success) {
          navigate(LIST_PATH, {
            state: { successMessage: SERVICE_TYPE_LABELS.CREATE_SUCCESS },
          });
          return;
        }

        const handledAsFieldError = result.fieldErrors
          ? applyFieldErrors(result.fieldErrors)
          : false;

        if (!handledAsFieldError) {
          setSnackbarError(result.error ?? '');
          setIsSnackbarOpen(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, navigate, applyFieldErrors]
  );

  const handleCancel = useCallback(() => {
    const hasContent =
      formData.serviceName.trim() !== '' || formData.description.trim() !== '';
    if (!hasContent) {
      navigate(LIST_PATH);
      return;
    }
    setIsCancelModalOpen(true);
  }, [formData.serviceName, formData.description, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(LIST_PATH);
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
  }, []);

  return {
    formData,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    handleServiceNameChange,
    handleDescriptionChange,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
  };
};
